import React from 'react'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Check, Cross, Download, MessageFilled, Pencil } from '@factorialco/f0-react/icons/app'
import {
  ApprovePurchaseRequestDocument,
  NewRequestPageDocument,
  ProcurementValueObjectsPurchaseRequestStatusEnum,
  PurchaseRequestDetailDocument,
  PurchaseRequestFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { UpdatePurchaseOrderModal } from 'modules/procurement/components/purchaseOrders/UpdateOrderModal'
import { NewPurchaseRequestModal } from 'modules/procurement/components/purchaseRequests/NewRequestModal'
import DownloadDocumentModal from 'modules/procurement/components/purchaseRequests/PurchaseRequestDetail/DownloadDocumentModal'
import RejectionReasonModal from 'modules/procurement/components/purchaseRequests/PurchaseRequestDetail/RejectionReasonModal'
import RequestDocumentModal from 'modules/procurement/components/purchaseRequests/PurchaseRequestDetail/RequestDocumentModal'
import RejectionModal from 'modules/procurement/components/purchaseRequests/RejectionModal'
import RequestChangesModal from 'modules/procurement/components/purchaseRequests/RequestChangesModal'
import { getPurchaseRequestStatusLabel } from 'modules/procurement/components/purchaseRequests/SpendingRequestStatusLabel'

interface PurchaseRequestDetailHeaderProps {
  purchaseRequest: PurchaseRequestFragment
  canManagePurchaseRequests?: boolean
  canManagePurchaseOrder?: boolean
  onlyOwnRequests: boolean
  isWorkflowsEnabled?: boolean
}

const PurchaseRequestDetailHeader = ({
  purchaseRequest,
  canManagePurchaseRequests = false,
  canManagePurchaseOrder = false,
  onlyOwnRequests,
  isWorkflowsEnabled = false,
}: PurchaseRequestDetailHeaderProps) => {
  const { company, access, employee } = useCurrentSession()
  const [rejectionModalOpen, setRejectionModalOpen] = React.useState(false)
  const [requestChangesModalOpen, setRequestChangesModalOpen] = React.useState(false)
  const [workflowRejectionModalOpen, setWorkflowRejectionModalOpen] = React.useState(false)
  const [editRequestModalOpen, setEditRequestModalOpen] = React.useState(false)
  const [uploadDocumentTaskModalOpen, setUploadDocumentTaskModalOpen] = React.useState(false)
  const [downloadDocumentTaskModalOpen, setDownloadDocumentTaskModalOpen] = React.useState(false)

  const documentTask = purchaseRequest?.uploadDocumentTask?.[0]
  const taskType = documentTask?.taskType
  const isDownloadTask = taskType === 'download'
  const isUploadTask = taskType === 'upload'

  const isCurrentUserAssignee = documentTask?.taskAssigneeIds?.includes(access.id) ?? false
  const isTaskTodo = documentTask?.taskStatus === 'todo'

  const canPerformTaskAction = !!documentTask && isCurrentUserAssignee && isTaskTodo

  const showUploadDocumentTaskActions = canPerformTaskAction && isUploadTask
  const showDownloadDocumentTaskActions = canPerformTaskAction && isDownloadTask
  const reviewRequestIds =
    purchaseRequest?.approvalProcess?.reviewRequests?.map((reviewRequest) => reviewRequest.id) ?? []

  const [approvePurchaseRequest] = useMutation(ApprovePurchaseRequestDocument, {
    extractorFn: (data) => data.procurementMutations.approveProcurementPurchaseRequest,
    refetchQueries: [PurchaseRequestDetailDocument],
  })

  const areChangesRequested = purchaseRequest?.status === 'changes_requested'
  const [isApprovalStatusUpdated, setIsApprovalStatusUpdated] = React.useState(false)
  const [pendingPurchaseOrderId, setPendingPurchaseOrderId] = React.useState<number | null>()

  const isPurchaseRequestOwner = purchaseRequest?.requesterEmployee?.id === employee.id
  const showRequestChangesActions = areChangesRequested && isPurchaseRequestOwner

  const { data } = useQuery(NewRequestPageDocument, {
    variables: { companyId: company.id, enabled: true },
    skip: !showRequestChangesActions,
  })

  const hasCustomFormsEnabled =
    data?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_procurement_custom_forms'
    )?.enabled ?? false

  const currencies = data?.apiCore?.currenciesConnection?.edges?.map((edge) => edge.node) || []
  const userCurrency =
    data?.apiCore?.currentsConnection?.nodes?.[0]?.employee?.legalEntity?.currency || ''

  const closeModal = () => {
    setPendingPurchaseOrderId(undefined)
  }

  const currentStep = purchaseRequest.approval?.steps.find((step) =>
    step.stepApprovers.some((approver) => approver.status === 'pending')
  )
  const currentStepAccesses = currentStep?.stepApprovers?.flatMap((approver) => approver.access?.id)

  const isCurrentAccessCurrentApprover = currentStepAccesses?.includes(access.id)

  const isApprovable =
    purchaseRequest &&
    purchaseRequest.status !== 'approved' &&
    purchaseRequest.status !== 'rejected'

  const canReview = isApprovable && canManagePurchaseRequests && isCurrentAccessCurrentApprover

  const showApprovalActions =
    !isApprovalStatusUpdated && purchaseRequest && canReview && !onlyOwnRequests

  // New workflow approval actions
  const hasReviewRequests = reviewRequestIds.length > 0

  const showWorkflowApprovalActions =
    !onlyOwnRequests &&
    isWorkflowsEnabled &&
    !isApprovalStatusUpdated &&
    purchaseRequest.status === 'pending' &&
    canManagePurchaseRequests &&
    hasReviewRequests

  const handleApprove = async () => {
    const result = await approvePurchaseRequest({
      variables: { id: purchaseRequest.id, companyId: company.id },
      refetchQueries: [PurchaseRequestDetailDocument],
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('procurement.purchase_requests.detail.responses.approve.error'),
      })
    })

    if (handled.success) {
      setIsApprovalStatusUpdated(true)

      const purchaseRequestStatus = handled.data.purchaseRequest?.status

      let message
      let finalApprover

      switch (purchaseRequestStatus) {
        case 'rejected':
          message = i18n.t('procurement.purchase_requests.detail.responses.reject.success')
          break
        case 'approved':
          if (canManagePurchaseOrder && handled.data.purchaseRequest?.purchaseOrder) {
            setPendingPurchaseOrderId(handled.data.purchaseRequest?.purchaseOrder.id)
          } else {
            message = i18n.t(
              'procurement.purchase_requests.detail.responses.approve.success_approved'
            )
          }
          finalApprover = true
          break
        default:
          message = i18n.t('procurement.purchase_requests.detail.responses.approve.success_pending')
          finalApprover = false
      }

      if (message) {
        app.setFlash({
          type: 'correct',
          message: message,
        })
      }

      track(trackingEvents.procurement.approvePurchaseRequest, {
        from: 'sidepanel',
        description: purchaseRequest?.description,
        cost: purchaseRequest?.cost.cents / 100,
        currency: purchaseRequest?.cost.currency,
        deadline: purchaseRequest?.deadline ? 'yes' : 'no',
        URL: purchaseRequest?.url ? 'yes' : 'no',
        additionalInformation: purchaseRequest?.additionalInformation,
        vendorDetailTaxId: purchaseRequest?.vendor?.taxId,
        attachments: purchaseRequest.files.length !== 0 ? 'yes' : 'no',
        finalApprover: finalApprover,
      })
    }
  }

  const metadata = [
    {
      label: i18n.t('procurement.purchase_requests.detail.info.request_date'),
      value: {
        type: 'date' as const,
        formattedDate: dateBackendDateTimeToUI(purchaseRequest.date),
      },
    },
    {
      label: i18n.t('procurement.purchase_requests.detail.info.deadline'),
      value: {
        type: 'date' as const,
        formattedDate: purchaseRequest.deadline
          ? dateBackendDateTimeToUI(purchaseRequest.deadline)
          : '-',
      },
    },
    {
      label: i18n.t('procurement.purchase_orders.detail.info.type'),
      value: {
        color: 'smoke' as const,
        label: purchaseRequest?.type?.name || '',
        type: 'dot-tag' as const,
      },
    },
  ]

  const getApprovalProcessStatus = (): ProcurementValueObjectsPurchaseRequestStatusEnum => {
    if (isWorkflowsEnabled && purchaseRequest.approvalProcess) {
      if (purchaseRequest.approvalProcess.decision) {
        return purchaseRequest.approvalProcess.decision
      }
      const hasChangesRequested = purchaseRequest.approvalProcess.reviewRequests?.some(
        (rr) => rr.decision === 'changes_requested'
      )
      if (hasChangesRequested) {
        return 'changes_requested'
      }
    }
    return purchaseRequest.status
  }

  const statusLabel = getPurchaseRequestStatusLabel(getApprovalProcessStatus())

  const getSecondaryActions = ():
    | Array<{
        icon: typeof Cross | typeof MessageFilled | typeof Pencil
        label: string
        onClick: () => void
      }>
    | undefined => {
    if (showApprovalActions) {
      return [
        {
          icon: Cross,
          label: i18n.t('procurement.purchase_requests.detail.actions.reject'),
          onClick: () => setRejectionModalOpen(true),
        },
      ]
    } else if (showWorkflowApprovalActions) {
      return [
        {
          icon: MessageFilled,
          label: i18n.t('procurement.purchase_requests.detail.actions.changes_requested'),
          onClick: () => setRequestChangesModalOpen(true),
        },
        {
          icon: Cross,
          label: i18n.t('procurement.purchase_requests.detail.actions.reject'),
          onClick: () => setWorkflowRejectionModalOpen(true),
        },
      ]
    }

    return undefined
  }

  const getPrimaryAction = ():
    | {
        icon?: typeof Check | typeof Pencil
        label: string
        onClick: () => void
      }
    | undefined => {
    if (showApprovalActions || showWorkflowApprovalActions) {
      return {
        icon: Check,
        label: i18n.t('procurement.purchase_requests.detail.actions.approve'),
        onClick: () => handleApprove(),
      }
    }

    if (showRequestChangesActions) {
      return {
        icon: Pencil,
        label: i18n.t('procurement.purchase_requests.detail.actions.edit_request'),
        onClick: () => setEditRequestModalOpen(true),
      }
    }

    if (showUploadDocumentTaskActions) {
      return {
        label: i18n.t('procurement.purchase_requests.detail.actions.upload_document_task'),
        onClick: () => setUploadDocumentTaskModalOpen(true),
      }
    }

    if (showDownloadDocumentTaskActions) {
      return {
        icon: Download,
        label: i18n.t('procurement.purchase_requests.detail.actions.download_document_task'),
        onClick: () => setDownloadDocumentTaskModalOpen(true),
      }
    }

    return undefined
  }

  return (
    <>
      <ResourceHeader
        title={purchaseRequest.description}
        status={{ text: statusLabel.label, label: statusLabel.label, variant: statusLabel.type }}
        metadata={metadata}
        primaryAction={getPrimaryAction()}
        secondaryActions={getSecondaryActions()}
      />

      <RequestChangesModal
        isOpen={requestChangesModalOpen}
        onClose={() => setRequestChangesModalOpen(false)}
        reviewRequestIds={reviewRequestIds}
        onSuccess={() => {
          setIsApprovalStatusUpdated(true)
        }}
      />

      {rejectionModalOpen && (
        <RejectionModal
          id={purchaseRequest.id}
          onClose={() => setRejectionModalOpen(false)}
          handleUpdateApprovalStatus={setIsApprovalStatusUpdated}
        />
      )}

      {workflowRejectionModalOpen && (
        <RejectionReasonModal
          isOpen={workflowRejectionModalOpen}
          onClose={() => setWorkflowRejectionModalOpen(false)}
          reviewRequestIds={reviewRequestIds}
          onSuccess={() => {
            setIsApprovalStatusUpdated(true)
            app.setFlash({
              type: 'correct',
              message: i18n.t('procurement.purchase_requests.detail.responses.reject.success'),
            })
          }}
        />
      )}

      {uploadDocumentTaskModalOpen && (
        <RequestDocumentModal
          isOpen={uploadDocumentTaskModalOpen}
          onClose={() => setUploadDocumentTaskModalOpen(false)}
          uploadDocumentTask={documentTask}
        />
      )}

      {downloadDocumentTaskModalOpen && (
        <DownloadDocumentModal
          isOpen={downloadDocumentTaskModalOpen}
          onClose={() => setDownloadDocumentTaskModalOpen(false)}
          documentTask={documentTask}
        />
      )}

      {pendingPurchaseOrderId && (
        <UpdatePurchaseOrderModal
          purchaseOrderId={pendingPurchaseOrderId}
          handleClose={closeModal}
        />
      )}

      {editRequestModalOpen && purchaseRequest.type && (
        <NewPurchaseRequestModal
          procurementType={purchaseRequest.type}
          currencies={currencies}
          defaultCurrency={purchaseRequest.cost?.currency || userCurrency}
          hasCustomFormsEnabled={hasCustomFormsEnabled}
          onClose={() => setEditRequestModalOpen(false)}
          existingPurchaseRequest={{
            id: purchaseRequest.id,
            description: purchaseRequest.description,
            cost: purchaseRequest.cost,
            url: purchaseRequest.url,
            deadline: purchaseRequest.deadline,
            additionalInformation: purchaseRequest.additionalInformation,
            vendor: purchaseRequest.vendor ?? null,
            files: purchaseRequest.files,
            formsMembership: purchaseRequest.formsMembership
              ? {
                  answers:
                    purchaseRequest.formsMembership.answers?.map((answer) => ({
                      id: Number(answer.id),
                      answer: answer.answer || '',
                      comment: null,
                      element: { id: Number(answer.element?.id || 0) },
                      elementOptionIds: null,
                    })) ?? null,
                }
              : null,
          }}
        />
      )}
    </>
  )
}

export default PurchaseRequestDetailHeader
